import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, apiClient } from '../baseQuery';

export const schoolAdminApi = createApi({
	reducerPath: 'schoolAdminApi',
	baseQuery: axiosBaseQuery,
	tagTypes: [
		'SchoolDashboard',
		'SchoolClasses',
		'SchoolTrainers',
		'SchoolStudents',
		'SchoolClassStudents',
		'SchoolAnalytics',
	],
	endpoints: (builder) => ({
		// Daily reports — read-only, already scoped to the caller's school and
		// filtered to status 'sent' by the backend.
		listSchoolReports: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/school-admin/reports',
				params: params || undefined,
			}),
		}),
		getSchoolReport: builder.query<any, string>({
			query: (id) => ({ url: `/school-admin/reports/${id}` }),
		}),
		getLatestSchoolReport: builder.query<any, void>({
			query: () => ({ url: '/school-admin/reports/latest' }),
		}),
		getSchoolDashboard: builder.query<any, void>({
			query: () => ({ url: '/school-admin/dashboard' }),
			providesTags: ['SchoolDashboard'],
		}),
		getSchoolClasses: builder.query<any, void>({
			query: () => ({ url: '/school-admin/classes' }),
			providesTags: ['SchoolClasses'],
		}),
		getSchoolTrainers: builder.query<any, void>({
			query: () => ({ url: '/school-admin/trainers' }),
			providesTags: ['SchoolTrainers'],
		}),
		getSchoolStudents: builder.query<any, void>({
			query: () => ({ url: '/school-admin/students' }),
			providesTags: ['SchoolStudents'],
		}),
		getSchoolClassStudents: builder.query<any, string>({
			query: (classId) => ({
				url: `/school-admin/classes/${classId}/students`,
			}),
			providesTags: (_result, _err, classId) => [
				{ type: 'SchoolClassStudents', id: classId },
			],
		}),
		getSchoolAnalytics: builder.query<any, void>({
			query: () => ({ url: '/school-admin/analytics' }),
			providesTags: ['SchoolAnalytics'],
		}),
		getSchoolClassCurriculumProgress: builder.query<
			any,
			{ classId: string; curriculumId: string }
		>({
			query: ({ classId, curriculumId }) => ({
				url: `/school-admin/analytics/class/${classId}/curriculum/${curriculumId}`,
			}),
		}),

		// ── Mutations ─
		createSchoolClass: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/school-admin/classes',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SchoolClasses', 'SchoolDashboard'],
		}),
		updateSchoolClass: builder.mutation<
			any,
			{ classId: string; data: Record<string, unknown> }
		>({
			query: ({ classId, data }) => ({
				url: `/school-admin/classes/${classId}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['SchoolClasses'],
		}),
		deleteSchoolClass: builder.mutation<any, string>({
			query: (id) => ({ url: `/school-admin/classes/${id}`, method: 'DELETE' }),
			invalidatesTags: ['SchoolClasses', 'SchoolDashboard'],
		}),
		addSchoolStudent: builder.mutation<
			any,
			{ classId: string; data: Record<string, unknown> }
		>({
			query: ({ classId, data }) => ({
				url: `/school-admin/classes/${classId}/students`,
				method: 'POST',
				data,
			}),
			invalidatesTags: (_result, _err, { classId }) => [
				{ type: 'SchoolClassStudents', id: classId },
				'SchoolStudents',
				'SchoolDashboard',
			],
		}),
		removeSchoolStudent: builder.mutation<
			any,
			{ classId: string; studentId: string }
		>({
			query: ({ classId, studentId }) => ({
				url: `/school-admin/classes/${classId}/students/${studentId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _err, { classId }) => [
				{ type: 'SchoolClassStudents', id: classId },
				'SchoolStudents',
			],
		}),
		createTrainer: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/school-admin/trainers',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SchoolTrainers', 'SchoolDashboard'],
		}),
		deleteTrainer: builder.mutation<any, string>({
			query: (id) => ({
				url: `/school-admin/trainers/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['SchoolTrainers', 'SchoolDashboard'],
		}),
		assignClassToTrainer: builder.mutation<
			any,
			{ trainerId: string; classId: string }
		>({
			query: ({ trainerId, classId }) => ({
				url: `/school-admin/trainers/${trainerId}/assign-class`,
				method: 'POST',
				data: { classId },
			}),
			invalidatesTags: ['SchoolTrainers', 'SchoolClasses'],
		}),
		transferStudentSection: builder.mutation<
			any,
			{ studentId: string; newClassId: string }
		>({
			query: ({ studentId, newClassId }) => ({
				url: `/school-admin/students/${studentId}/transfer-section`,
				method: 'PATCH',
				data: { newClassId },
			}),
			invalidatesTags: ['SchoolStudents', 'SchoolClasses'],
		}),
		resetStudentPin: builder.mutation<any, string>({
			query: (studentId) => ({
				url: `/school-admin/students/${studentId}/reset-pin`,
				method: 'POST',
			}),
		}),
		resetParentPassword: builder.mutation<any, string>({
			query: (studentId) => ({
				url: `/school-admin/students/${studentId}/reset-parent-password`,
				method: 'POST',
			}),
		}),
		getParentCredentials: builder.query<
			{
				parentId: string;
				fullName: string;
				alforaaId: string;
				email: string;
				mustChangePassword: boolean;
			},
			string
		>({
			query: (studentId) => ({
				url: `/school-admin/students/${studentId}/parent-credentials`,
				method: 'GET',
			}),
		}),
		resendParentCredentials: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/school-admin/parents/resend-credentials',
				method: 'POST',
				data: body,
			}),
		}),
		uploadSchoolRoster: builder.mutation<any, File>({
			queryFn: async (file) => {
				try {
					const formData = new FormData();
					formData.append('file', file);
					const result = await apiClient.post(
						'/school-admin/students/upload',
						formData,
						{ headers: { 'Content-Type': 'multipart/form-data' } },
					);
					return { data: result.data };
				} catch (err: any) {
					return {
						error: {
							status: err.response?.status,
							data: err.response?.data,
							message: err.message,
						},
					};
				}
			},
			invalidatesTags: ['SchoolStudents', 'SchoolDashboard'],
		}),
		getSchoolStudentSessionReview: builder.query<
			any,
			{ studentId: string; sessionId: string }
		>({
			query: ({ studentId, sessionId }) => ({
				url: `/school-admin/students/${studentId}/sessions/${sessionId}/review`,
			}),
		}),
		getSchoolStudentCurriculumProgress: builder.query<any, string>({
			query: (studentId) => ({
				url: `/school-admin/students/${studentId}/curriculum-progress`,
			}),
		}),
	}),
});

export const {
	useListSchoolReportsQuery,
	useGetSchoolReportQuery,
	useGetLatestSchoolReportQuery,
	useGetSchoolDashboardQuery,
	useGetSchoolClassesQuery,
	useGetSchoolTrainersQuery,
	useGetSchoolStudentsQuery,
	useGetSchoolClassStudentsQuery,
	useGetSchoolAnalyticsQuery,
	useGetSchoolClassCurriculumProgressQuery,
	useLazyGetSchoolClassCurriculumProgressQuery,
	useCreateSchoolClassMutation,
	useUpdateSchoolClassMutation,
	useDeleteSchoolClassMutation,
	useAddSchoolStudentMutation,
	useRemoveSchoolStudentMutation,
	useCreateTrainerMutation,
	useDeleteTrainerMutation,
	useAssignClassToTrainerMutation,
	useResetStudentPinMutation,
	useResetParentPasswordMutation,
	useGetParentCredentialsQuery,
	useLazyGetParentCredentialsQuery,
	useResendParentCredentialsMutation,
	useUploadSchoolRosterMutation,
	useGetSchoolStudentSessionReviewQuery,
	useGetSchoolStudentCurriculumProgressQuery,
	useTransferStudentSectionMutation,
} = schoolAdminApi;
